import { useState } from "react";
import { ArrowRightLeft, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { Field } from "@/components/common";
import { MEALS, MEAL_LABEL, dayLong } from "@/lib/meals";

export default function SlotDialog({ hubId, week, slot, recipes, onClose, onSaved }) {
  const filled = week.slots.find((s) => s.date === slot.date && s.meal === slot.meal);
  const [recipeId, setRecipeId] = useState(filled?.recipe_id || "");
  const [text, setText] = useState(filled && !filled.recipe_id ? filled.name : "");
  const [cook, setCook] = useState(filled?.cook_key || "");
  const [note, setNote] = useState(filled?.note || "");
  const [moveTo, setMoveTo] = useState("");
  const [busy, setBusy] = useState("");
  const base = `/organizations/${hubId}/meals`;
  const run = async (key, fn, msg) => {
    setBusy(key);
    try { await fn(); toast.success(msg); onSaved(); onClose(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const save = () => run("save", () => api.put(`${base}/slots`, { date: slot.date, meal: slot.meal, recipe_id: recipeId || null, text: recipeId ? null : text, cook_key: cook || null, note }), "Meal saved");
  const move = () => { const [d, m] = moveTo.split("|"); return run("move", () => api.post(`${base}/slots/move`, { from_date: slot.date, from_meal: slot.meal, to_date: d, to_meal: m }), "Meal moved"); };
  const clear = () => run("clear", () => api.delete(`${base}/slots/${slot.date}/${slot.meal}`), "Slot cleared");
  const targets = week.days.flatMap((d) => MEALS.map((m) => ({ v: `${d}|${m}`, label: `${dayLong(d)} · ${MEAL_LABEL[m]}` }))).filter((t) => t.v !== `${slot.date}|${slot.meal}`);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent bigClose className="max-h-[92vh] overflow-y-auto sm:max-w-md" data-testid="slot-dialog">
        <DialogHeader><DialogTitle className="font-display">{dayLong(slot.date)} · {MEAL_LABEL[slot.meal]}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="From the recipe box">
            <select className="q-input w-full" value={recipeId} onChange={(e) => setRecipeId(e.target.value)} data-testid="slot-recipe-select">
              <option value="">— Type a meal instead —</option>
              {recipes.map((r) => <option key={r.id} value={r.id}>{r.emoji ? `${r.emoji} ` : ""}{r.name}</option>)}
            </select>
          </Field>
          {!recipeId && <Field label="Or type it"><input className="q-input w-full" value={text} maxLength={80} placeholder="Leftovers, Eat out…" onChange={(e) => setText(e.target.value)} data-testid="slot-text-input" /></Field>}
          <Field label="Who cooks (optional)">
            <select className="q-input w-full" value={cook} onChange={(e) => setCook(e.target.value)} data-testid="slot-cook-select">
              <option value="">Nobody picked</option>
              {week.people.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
            </select>
          </Field>
          <Field label="Note (optional)"><input className="q-input w-full" value={note} maxLength={140} onChange={(e) => setNote(e.target.value)} data-testid="slot-note-input" /></Field>
          <button className="q-btn q-btn-primary w-full" disabled={!!busy || (!recipeId && !text.trim())} onClick={save} data-testid="slot-save-btn">{busy === "save" && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
          {filled && (
            <div className="space-y-3 border-t border-q-line pt-4">
              <Field label="Move to…">
                <div className="flex gap-2">
                  <select className="q-input min-w-0 flex-1" value={moveTo} onChange={(e) => setMoveTo(e.target.value)} data-testid="slot-move-select">
                    <option value="">Pick a slot</option>
                    {targets.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                  </select>
                  <button className="q-btn q-btn-secondary" disabled={!moveTo || !!busy} onClick={move} data-testid="slot-move-btn"><ArrowRightLeft className="h-4 w-4" /> Move</button>
                </div>
              </Field>
              <button className="q-btn q-btn-ghost w-full" disabled={!!busy} onClick={clear} data-testid="slot-clear-btn"><Trash2 className="h-4 w-4" /> Clear this slot</button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
